import { OptionType, ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { AcceptedFiles } from '@insurancemenu/imenu-components-lib/dist/types/FileUpload';
import axios from 'axios';
import { Domain } from 'types/domainServiceV2';
import { uploadFiles } from 'global/utils/uploadFiles';
import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { RootStore } from 'store/rootStore';
import { ToastInfo } from 'types/general';
import { FileExchangeService } from 'types/exchangeService';
import { getAllDomains } from 'api/operations/domainServiceV2';
import { domainServiceHelper } from 'global/services-helpers';
import { UploadUrls } from 'types/carrierFileService';
import { COMMON_URLS } from '@insurancemenu/imenu-components-lib/dist/utils/urls';

// It's a common logic for all dashboards on the files tracking page.
export default class FilesTrackingController {
  rootStore: RootStore;
  loading = false;
  domains: Domain[] = [];

  // Upload Files Modal properties
  /**
   * Clears uploaded files from the modal's dropzone.
   * Use this flag to remove files after upload from the modal.
   */
  isFileUploadReset = false;
  isUploadModalOpen = false;

  // Toast Info
  toastInfo: ToastInfo = {
    showToast: false,
    toastType: ToastColorTypes.SUCCESS,
    toastMessage: '',
  };

  private setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  // Upload Modal methods
  public setIsFileUploadReset(status: boolean): void {
    this.isFileUploadReset = status;
  }

  public openUploadModal(): void {
    this.isUploadModalOpen = true;
  }

  public closeUploadModal(): void {
    this.isUploadModalOpen = false;
  }

  // Toast methods
  public showToast(toastMessage: string, type: ToastColorTypes): void {
    this.resetToastInfo();
    this.toastInfo.showToast = true;
    this.toastInfo.toastMessage = toastMessage;
    this.toastInfo.toastType = type;
  }

  public closeToast(): void {
    this.toastInfo.showToast = false;
    this.resetToastInfo();
  }

  private resetToastInfo(): void {
    this.toastInfo.toastType = ToastColorTypes.SUCCESS;
    this.toastInfo.toastMessage = '';
  }

  /**
   * Retrieve information about all domains to display on each dashboard.
   */
  public async getAllDomains(): Promise<void> {
    const domains = await getAllDomains();
    runInAction(() => {
      this.domains = domains;
    });
  }

  /**
   * Sponsor Options
   */
  public getSponsorOptions(): OptionType[] {
    const convertedDomains = this.domains.map((domain) => toJS(domain)).slice();
    return domainServiceHelper.getSponsorOptions(convertedDomains);
  }

  /**
   * Sponsor Options with parameter 'all' that allows to get data for all sponsors
   */
  public getSponsorOptionsWithOptionAll(): OptionType[] {
    const convertedDomains = this.domains.map((domain) => toJS(domain)).slice();
    return [{ value: '', label: 'All' }, ...domainServiceHelper.getSponsorOptions(convertedDomains)];
  }

  // Upload files methods
  public uploadSponsorFiles = async (
    files: AcceptedFiles,
    sponsorId: string,
    service: Exclude<FileExchangeService, FileExchangeService.CSV>,
    onUpdate: (sponsorId: string) => void,
  ): Promise<void> => {
    this.closeToast();
    const uploadedFileNames = Object.keys(files).join(':');
    this.setLoading(true);
    // List of upload links
    const UploadLinksMap: Record<Exclude<FileExchangeService, FileExchangeService.CSV>, string> = Object.freeze({
      [FileExchangeService.EDI]: `https://${COMMON_URLS.api}/edi/Prod/upload?fileNames=${uploadedFileNames}&sponsorId=${sponsorId}`,
      [FileExchangeService.PDF]: `https://${COMMON_URLS.api}/pdfRead/Prod/upload?fileNames=${uploadedFileNames}&sponsorId=${sponsorId}`,
    });

    // Handle errors in first REST request
    try {
      // Send file names and get list of urls
      const res = await axios<UploadUrls>({
        url: UploadLinksMap[service],
        withCredentials: true,
      });
      // Handle errors in uploadFiles
      try {
        await uploadFiles(files, res.data, () => {
          this.closeToast();
          this.showToast(
            Object.keys(files).length === 1 ? 'File was successfully uploaded' : 'Files were successfully uploaded',
            ToastColorTypes.SUCCESS,
          );

          this.setLoading(false);
          this.setIsFileUploadReset(true);
          onUpdate(sponsorId);
        });
      } catch (error) {
        this.onUploadErrorCatch();
      }
    } catch (error) {
      this.onUploadErrorCatch();
    }
  };

  private onUploadErrorCatch(): void {
    this.showToast('An issue occurred during the file upload process.', ToastColorTypes.WARNING);
    this.setLoading(false);
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
